import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { createCheckout, validateDiscount } from "@/lib/api";
import { Loader2, Lock, Download, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

export const CheckoutDialog = ({ item, onClose, initialCode }) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (item) {
      setEmail("");
      setCode(initialCode || "");
      setApplied(null);
    }
  }, [item, initialCode]);

  if (!item) return null;

  const price = item.price || 0;
  const finalPrice = applied ? price * (1 - applied.percent_off / 100) : price;

  const applyCode = async () => {
    if (!code.trim()) return;
    const res = await validateDiscount(code.trim());
    if (res.valid) {
      setApplied(res);
      toast.success(`${res.percent_off}% discount applied`);
    } else {
      setApplied(null);
      toast.error("Invalid discount code");
    }
  };

  const submit = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const res = await createCheckout({
        item_type: item.item_type,
        slug: item.slug,
        email,
        discount_code: applied ? code.trim() : null,
        origin_url: window.location.origin,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md rounded-none border-black/10" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Secure Checkout</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          <div className="flex items-start justify-between border-b border-black/10 pb-4">
            <div>
              <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] text-[11px]">{item.item_type === "bundle" ? "Bundle" : "Guide"}</p>
              <p className="font-display text-lg">{item.title}</p>
              <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Download className="h-3 w-3" /> Instant download</span>
            </div>
            <div className="text-right">
              {applied && <p className="text-xs text-muted-foreground line-through">${price.toFixed(2)}</p>}
              <p className="font-display text-2xl">${finalPrice.toFixed(2)}</p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email for delivery</label>
            <Input data-testid="checkout-email-input" type="email" placeholder="you@email.com" value={email}
              onChange={(e) => setEmail(e.target.value)} className="rounded-none h-11 border-black/20 focus-visible:ring-black" />
          </div>

          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Discount code</label>
            <div className="flex gap-2">
              <Input data-testid="checkout-discount-input" placeholder="WELCOME10" value={code}
                onChange={(e) => setCode(e.target.value)} className="rounded-none h-11 border-black/20 focus-visible:ring-black" />
              <Button data-testid="checkout-apply-discount" variant="outline" onClick={applyCode} className="rounded-none h-11 border-black/20">Apply</Button>
            </div>
            {applied && <p className="flex items-center gap-1 text-xs text-emerald-600"><BadgeCheck className="h-3.5 w-3.5" /> {applied.percent_off}% off applied</p>}
          </div>

          <Button data-testid="checkout-pay-button" onClick={submit} disabled={loading}
            className="w-full rounded-none h-12 bg-primary text-primary-foreground hover:bg-primary/90 text-sm tracking-wide">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" /> Pay ${finalPrice.toFixed(2)}</>}
          </Button>
          <p className="text-center text-[11px] text-muted-foreground">Secured by Stripe · Test card 4242 4242 4242 4242</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
